import {useEffect} from 'react';
import {Row} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import Select from '../../../app/common/components/select/Select';
import {SetFieldValue} from '../../../app/types/Forms';
import {BuOption} from '../../../app/types/Customer';
import {useCustomerList} from '../../../app/hooks/useCustomerManagerHook';
import {useGetMe} from '../../../app/hooks/useGetMe';

interface SelectBuProps {
  name: string;
  value?: BuOption;
  setFieldValue: SetFieldValue;
  isDisabled?: boolean;
  touched?: boolean;
  error?: string;
  serviceBuId?: string | number;
}

const SelectBu = ({
  name,
  value,
  setFieldValue,
  isDisabled,
  touched,
  error,
  serviceBuId,
}: SelectBuProps): JSX.Element | null => {
  const intl = useIntl();
  const {isLoading, data: customers} = useCustomerList();
  const me = useGetMe();

  // Pré-sélectionne la BU par défaut (celle de l'utilisateur courant, sinon la première).
  // L'effet s'exécute même lorsque le sélecteur est masqué (une seule BU),
  // de sorte que le formulaire envoie toujours un customerId.
  useEffect(() => {
    if (value || !customers || customers.length === 0) {
      return;
    }
    const defaultBu =
      customers.find((bu) => bu.id.toString() === me?.id?.toString()) ?? customers[0];
    setFieldValue(name, defaultBu);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, me?.id]);

  // Pré-remplit la BU à partir du service sélectionné (sa BU prime sur le défaut).
  useEffect(() => {
    if (!serviceBuId || !customers) {
      return;
    }
    const match = customers.find((bu) => bu.id.toString() === serviceBuId.toString());
    if (match && match.id.toString() !== value?.id?.toString()) {
      setFieldValue(name, match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceBuId, customers]);

  // Si l'utilisateur n'a qu'une seule BU, aucun choix n'est nécessaire : on masque le sélecteur.
  if (!customers || customers.length <= 1) {
    return null;
  }

  return (
    <Row className={'mt-3'}>
      <Select<BuOption>
        label={intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.BU'})}
        isLoading={isLoading}
        isDisabled={isDisabled}
        isClearable={false}
        isSearchable={true}
        name={name}
        onChange={(bu) => setFieldValue(name, bu ?? undefined)}
        value={value}
        getOptionLabel={(bu) => `${bu.id} - ${bu.name}`}
        getOptionValue={(bu) => bu.id.toString()}
        options={customers}
        placeholder={intl.formatMessage({id: 'ADMINISTRATIVE.RECLAMATIONS.BU.PLACEHOLDER'})}
        touched={touched}
        error={error}
        isMulti={false}
      />
    </Row>
  );
};

export default SelectBu;
